import type { DrawMatch, DrawResult, DrawStage, MatchProgression } from '@/draw/types';
import type { SeatPlanEntry } from '@/draw/utils/seatPlan';

export type RoundAbbrev =
  | { kind: 'final' }
  | { kind: 'sf' }
  | { kind: 'qf' }
  | { kind: 'roundOf'; players: number }
  | { kind: 'round'; number: number };

export interface PlaceBand {
  from: number;
  to: number;
}

export interface MatchCode {
  /** The stored `draw_match_id` (A1, qual-B2): what the public page prints. */
  id?: string;
  stageId: string;
  round: RoundAbbrev;
  /** 1-based position in its round; null when the round has a single match. */
  index: number | null;
  /** Set for a match inside a nested playoff, so "SF1" reads "5th–8th · SF1". */
  places?: PlaceBand | null;
}

export type SlotSource =
  | { kind: 'pool'; pool: number; rank: number } // rank is 1-based
  | { kind: 'match'; outcome: 'winner' | 'loser'; code: MatchCode; otherStageName?: string };

/** Where a match's loser plays next; `otherStageName` is set when that is another stage. */
export interface LoserTarget {
  matchId: string;
  code: MatchCode;
  otherStageName?: string;
}

export interface LadderMatch {
  match: DrawMatch;
  code: MatchCode;
  /** Where this match's winner plays next inside the same ladder. */
  next: MatchCode | null;
  /** Where this match's loser plays next (a plate, a playoff), in any stage. */
  loserTo: LoserTarget | null;
  sources: [SlotSource | null, SlotSource | null];
}

export interface LadderRound {
  roundId: string;
  name: string;
  abbrev: RoundAbbrev;
  matches: LadderMatch[];
  played: number;
  total: number;
  /** Matches with exactly one bye seat. */
  byes: number;
  live: boolean;
  /** Playoffs whose losers come from this round (deeper playoffs flattened here too). */
  feeders: Ladder[];
}

export interface Ladder {
  stage: DrawStage;
  places: PlaceBand | null;
  rounds: LadderRound[];
  currentRound: number;
  /** Every non-bye match is done; the current round then only picks the default tab. */
  finished: boolean;
  /** 'tree' = halving rounds the new view draws; 'legacy' = keep BracketStageVisualization. */
  layout: 'tree' | 'legacy';
}

export interface DrawLadders {
  pools: DrawStage[];
  ladders: Ladder[];
}

const byOrder = (a: DrawStage, b: DrawStage) => (a.order ?? 0) - (b.order ?? 0);
const byPosition = (a: DrawMatch, b: DrawMatch) => a.position - b.position;
const sortedRounds = (s: DrawStage) => [...s.rounds].sort((a, b) => a.roundNumber - b.roundNumber);

/** One bye seat: the match renders as a single line and the lone team goes through. */
export const hasOneBye = (match: DrawMatch): boolean => !!match.player1?.isBye !== !!match.player2?.isBye;

/** Game slots every card in a round shares: the highest known format, never fewer than recorded games; 3 when unknown. */
export function roundGameSlots(round: Pick<LadderRound, 'matches'>): number {
  let slots = 0;
  for (const { match } of round.matches) {
    slots = Math.max(
      slots,
      match.bestOf ?? 0,
      match.eventMatchSchedule?.best_of ?? 0,
      match.score?.length ?? 0,
    );
  }
  return slots > 0 ? slots : 3;
}

export interface FeederSection {
  round: LadderRound;
  roundIndex: number;
  feeders: Ladder[];
}

/**
 * The nested playoffs to list under a ladder, best places first. A later round's losers place
 * higher than an earlier round's, so the last feeding round comes first, and each round's playoffs
 * sort by their places (the generator stores a bracket's sub-playoffs breadth-first, 25/26 before
 * 23/24). `skip` is the playoff drawn elsewhere: the 3rd/4th card under the Final.
 */
export function feederSections(ladder: Ladder, skip: Ladder | null = null): FeederSection[] {
  const place = (f: Ladder) => f.places?.from ?? Number.MAX_SAFE_INTEGER;
  const sections: FeederSection[] = [];
  for (let roundIndex = ladder.rounds.length - 1; roundIndex >= 0; roundIndex--) {
    const round = ladder.rounds[roundIndex];
    const feeders = round.feeders.filter((f) => f !== skip).sort((a, b) => place(a) - place(b));
    if (feeders.length > 0) sections.push({ round, roundIndex, feeders });
  }
  return sections;
}

/** The one-match playoff of SF losers (3rd/4th), drawn under the Final instead of as a nested ladder. */
export function thirdPlaceFeeder(ladder: Ladder): Ladder | null {
  if (ladder.rounds.length < 2) return null;
  const feeder = ladder.rounds[ladder.rounds.length - 2].feeders.find(
    (f) => f.stage.context === 'playoff' && f.rounds.reduce((n, r) => n + r.matches.length, 0) === 1 && f.rounds.length === 1,
  );
  return feeder ?? null;
}

export function isMatchDone(match: DrawMatch): boolean {
  return match.status === 'completed' || match.isBye === true || match.isWalkover === true;
}

function isTreeShaped(stage: DrawStage): boolean {
  if (stage.type !== 'elimination' || stage.ladderTemplate || stage.settings?.ladderTemplate) return false;
  const counts = sortedRounds(stage).map((r) => r.matches.length);
  if (counts.length === 0 || counts[counts.length - 1] !== 1) return false;
  return counts.every((c, i) => i === 0 || counts[i - 1] === c * 2);
}

function roundAbbrev(matchCount: number, roundNumber: number, tree: boolean): RoundAbbrev {
  if (!tree) return { kind: 'round', number: roundNumber };
  if (matchCount === 1) return { kind: 'final' };
  if (matchCount === 2) return { kind: 'sf' };
  if (matchCount === 4) return { kind: 'qf' };
  return { kind: 'roundOf', players: matchCount * 2 };
}

interface Where {
  stage: DrawStage;
  roundIndex: number;
  roundNumber: number;
  index: number;
  roundSize: number;
}

export function buildDrawLadders(draw: Pick<DrawResult, 'stages' | 'matchProgression'>): DrawLadders {
  const stages = [...draw.stages].sort(byOrder);
  const pools = stages.filter((s) => s.type === 'round_robin');
  const knockouts = stages.filter((s) => s.type === 'elimination');

  const where = new Map<string, Where>();
  for (const stage of knockouts) {
    sortedRounds(stage).forEach((round, roundIndex) => {
      [...round.matches].sort(byPosition).forEach((match, index) => {
        where.set(match.matchId, { stage, roundIndex, roundNumber: round.roundNumber, index, roundSize: round.matches.length });
      });
    });
  }

  const into = new Map<string, MatchProgression>();
  const winnerTo = new Map<string, string>();
  const loserTo = new Map<string, string>();
  for (const p of draw.matchProgression ?? []) {
    into.set(`${p.toMatchId}:${p.position}`, p);
    (p.outcome === 'winner' ? winnerTo : loserTo).set(p.fromMatchId, p.toMatchId);
  }

  // The single (stage, round) whose losers enter this stage's first round; null when mixed or none.
  const feedMemo = new Map<string, { stage: DrawStage; roundIndex: number } | null>();
  const feedOf = (stage: DrawStage) => {
    if (feedMemo.has(stage.stageId)) return feedMemo.get(stage.stageId)!;
    const first = sortedRounds(stage)[0];
    const keys = new Set<string>();
    let found: { stage: DrawStage; roundIndex: number } | null = null;
    for (const match of first?.matches ?? []) {
      for (const position of ['player1', 'player2'] as const) {
        const p = into.get(`${match.matchId}:${position}`);
        const from = p && p.outcome === 'loser' ? where.get(p.fromMatchId) : undefined;
        if (!from || from.stage.stageId === stage.stageId) continue;
        keys.add(`${from.stage.stageId}:${from.roundIndex}`);
        found = { stage: from.stage, roundIndex: from.roundIndex };
      }
    }
    const result = keys.size === 1 ? found : null;
    feedMemo.set(stage.stageId, result);
    return result;
  };

  const isNested = (stage: DrawStage) =>
    stage.context === 'playoff' && isTreeShaped(stage) && feedOf(stage) !== null;

  const bandMemo = new Map<string, PlaceBand | null>();
  const bandOf = (stage: DrawStage, depth = 0): PlaceBand | null => {
    if (bandMemo.has(stage.stageId)) return bandMemo.get(stage.stageId)!;
    let band: PlaceBand | null = null;
    const stored = stage.settings?.placementBand as Partial<PlaceBand> | undefined;
    if (stored && Number.isFinite(stored.from) && Number.isFinite(stored.to)) {
      band = { from: stored.from!, to: stored.to! };
    } else if (isTreeShaped(stage) && stage.context === 'main') {
      band = { from: 1, to: sortedRounds(stage)[0].matches.length * 2 };
    } else if (isTreeShaped(stage) && depth < 8) {
      const feed = feedOf(stage);
      const parent = feed ? bandOf(feed.stage, depth + 1) : null;
      if (feed && parent) {
        const k = sortedRounds(feed.stage)[feed.roundIndex].matches.length;
        band = { from: parent.from + k, to: parent.from + 2 * k - 1 };
      }
    }
    bandMemo.set(stage.stageId, band);
    return band;
  };

  const codeOf = (matchId: string): MatchCode | null => {
    const at = where.get(matchId);
    if (!at) return null;
    const code: MatchCode = {
      id: matchId,
      stageId: at.stage.stageId,
      round: roundAbbrev(at.roundSize, at.roundNumber, isTreeShaped(at.stage)),
      index: at.roundSize > 1 ? at.index + 1 : null,
    };
    if (isNested(at.stage)) code.places = bandOf(at.stage);
    return code;
  };

  const sourceFor = (
    stage: DrawStage,
    roundIndex: number,
    match: DrawMatch,
    position: 'player1' | 'player2',
  ): SlotSource | null => {
    const p = into.get(`${match.matchId}:${position}`);
    const from = p ? where.get(p.fromMatchId) : undefined;
    if (p && from) {
      const source: SlotSource = { kind: 'match', outcome: p.outcome, code: codeOf(p.fromMatchId)! };
      if (from.stage.stageId !== stage.stageId) source.otherStageName = from.stage.name;
      return source;
    }
    if (roundIndex === 0) {
      // The seat plan indexes round 1 in its stored match order, as BracketVisualization does.
      const seatIndex = sortedRounds(stage)[0].matches.findIndex((x) => x.matchId === match.matchId);
      const plan = stage.settings?.seatPlan as SeatPlanEntry[] | undefined;
      const seat = plan?.find((e) => e.matchIndex === seatIndex && e.position === position);
      if (seat) return { kind: 'pool', pool: seat.pool, rank: seat.rank + 1 };
    }
    return null;
  };

  const loserTargetOf = (stage: DrawStage, matchId: string): LoserTarget | null => {
    const target = loserTo.get(matchId);
    const at = target ? where.get(target) : undefined;
    if (!target || !at) return null;
    const result: LoserTarget = { matchId: target, code: codeOf(target)! };
    if (at.stage.stageId !== stage.stageId) result.otherStageName = at.stage.name;
    return result;
  };

  const buildLadder = (stage: DrawStage): Ladder => {
    const tree = isTreeShaped(stage);
    const rounds: LadderRound[] = sortedRounds(stage).map((round, roundIndex) => {
      const matches = [...round.matches].sort(byPosition);
      const real = matches.filter((x) => !x.isBye);
      return {
        roundId: round.roundId,
        name: round.name,
        abbrev: roundAbbrev(matches.length, round.roundNumber, tree),
        matches: matches.map((match) => {
          const target = winnerTo.get(match.matchId);
          const next = target && where.get(target)?.stage.stageId === stage.stageId ? codeOf(target) : null;
          return {
            match,
            code: codeOf(match.matchId)!,
            next,
            loserTo: loserTargetOf(stage, match.matchId),
            sources: [
              sourceFor(stage, roundIndex, match, 'player1'),
              sourceFor(stage, roundIndex, match, 'player2'),
            ],
          };
        }),
        played: real.filter((x) => x.status === 'completed').length,
        total: real.length,
        byes: matches.filter(hasOneBye).length,
        live: matches.some((x) => x.status === 'in_progress'),
        feeders: [],
      };
    });
    const current = rounds.findIndex((r) => r.matches.some((x) => !isMatchDone(x.match)));
    return {
      stage,
      places: bandOf(stage),
      rounds,
      currentRound: current < 0 ? Math.max(0, rounds.length - 1) : current,
      finished: current < 0,
      layout: tree ? 'tree' : 'legacy',
    };
  };

  const ladders = knockouts.filter((s) => !isNested(s)).map(buildLadder);
  for (const stage of knockouts.filter(isNested)) {
    let feed = feedOf(stage)!;
    for (let guard = 0; isNested(feed.stage) && guard < 8; guard++) feed = feedOf(feed.stage)!;
    const root = ladders.find((x) => x.stage.stageId === feed.stage.stageId);
    if (root) root.rounds[feed.roundIndex].feeders.push(buildLadder(stage));
    else ladders.push(buildLadder(stage));
  }

  return { pools, ladders };
}
